import React, { useEffect, useMemo } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import { Button, Stack, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { object, string } from "yup";

import {postConvienceFee,putConvienceFee} from "../../../../Api/Signin/Transaction.Api";
import useLoadingState from "../../../../Utils/Hooks/useLodingState";
import { getProviderDisplayName } from "../../../../Utils/WordFormatter/ProviderAlias";
import {NumberInputType3,NumberInputType4} from "../../../atoms/Input/NumberInput";
import { SelectInput } from "../../../atoms/Input/SelectInput";

export const ConvenienceFeeForm = ({
  t,
  mode,
  selectedFee,
  providers,
  onCancel,
  onSuccess,
}) => {
  const isEdit = mode === "edit";
  const { startLoading, stopLoading, LoadingState } = useLoadingState();

  const validationSchema = object({
    providerId: string().required(t("validation:required-field")),
    type: string().required(t("validation:required-field")),
    value: string()
      .required(t("validation:required-field"))
      .test(
        "is-valid-number",
        t("validation:tax-invalid-character"),
        function (value) {
          if (value == null || value === "") return false;

          const normalized = value.toString().replace(/,/g, "").trim();

          if (!/^\d+(\.\d{1,2})?$/.test(normalized)) {
            return false;
          }

          const numericValue = Number(normalized);
          const currentType = this.parent.type;

          if (Number.isNaN(numericValue) || numericValue < 0) {
            return false;
          }

          if (currentType === "Percentage" && numericValue > 100) {
            return this.createError({
              message: t("validation:percentage-cannot-exceed-100"),
            });
          }

          return true;
        },
      ),
  });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setError,
    trigger,
    clearErrors,
  } = useForm({
    defaultValues: {
      providerId: "",
      type: "Percentage",
      value: "",
    },
    resolver: yupResolver(validationSchema),
    mode: "onChange",
  });

  const selectedType = watch("type");
  const selectedProviderId = watch("providerId");
  const selectedValue = watch("value");

  const normalizeNumericInput = (value) => {
    if (value == null) return "";
    return value.toString().replace(/,/g, "").trim();
  };

  const getNormalizedFormSnapshot = ({
    providerId = "",
    type = "Percentage",
    value = "",
  }) => ({
    providerId: providerId || "",
    type: type || "Percentage",
    value: normalizeNumericInput(value || ""),
  });

  const getNormalizedSelectedFeeSnapshot = (fee) => {
    if (!fee) {
      return {
        providerId: "",
        type: "Percentage",
        value: "",
      };
    }

    const isFixed = fee.type === "Fixed";
    const rawValue = isFixed
      ? fee.amount?.toString() || ""
      : fee.percentage?.toString() || "";

    return {
      providerId: fee.providerId || "",
      type: fee.type || "Percentage",
      value: normalizeNumericInput(rawValue),
    };
  };

  useEffect(() => {
    clearErrors("value");

    if (selectedValue !== "" && selectedValue != null) {
      trigger("value");
    }
  }, [selectedType, selectedValue, trigger, clearErrors]);

  const isFirstDoctorProvider = providers
    ?.find((p) => p.id === selectedProviderId)
    ?.providerName?.trim()
    ?.toLowerCase()
    ?.startsWith("first doctor");

  useEffect(() => {
    const firstDoctorProvider = providers.find((p) =>
      p.providerName?.toLowerCase().startsWith("first doctor"),
    );

    if (isEdit && selectedFee) {
      const isFixed = selectedFee.type === "Fixed";
      const rawValue = isFixed
        ? selectedFee.amount?.toString() || ""
        : selectedFee.percentage?.toString() || "";

      reset({
        providerId: selectedFee.providerId || "",
        type: selectedFee.type || "Percentage",
        value: rawValue,
      });
    } else {
      reset({
        providerId: firstDoctorProvider?.id || "",
        type: "Percentage",
        value: "",
      });
    }
  }, [isEdit, selectedFee, providers, reset]);

  useEffect(() => {
    if (isFirstDoctorProvider && selectedType !== "Percentage") {
      reset(
        {
          providerId: selectedProviderId,
          type: "Percentage",
          value: "",
        },
        {
          keepErrors: true,
          keepDirty: true,
          keepTouched: true,
        },
      );
    }
  }, [isFirstDoctorProvider, selectedType, selectedProviderId, reset]);

  const hasChanges = useMemo(() => {
    if (!isEdit || !selectedFee) return true;

    const original = getNormalizedSelectedFeeSnapshot(selectedFee);
    const current = getNormalizedFormSnapshot({
      providerId: selectedProviderId,
      type: selectedType,
      value: selectedValue,
    });

    return (
      original.providerId !== current.providerId ||
      original.type !== current.type ||
      original.value !== current.value
    );
  }, [isEdit, selectedFee, selectedProviderId, selectedType, selectedValue]);

  const submitHandler = async (formData) => {
    try {
      if (isEdit && !hasChanges) {
        return;
      }

      startLoading({ btnName: "saveConvenienceFee" });

      const provider = providers.find(
        (item) => item.id === formData.providerId,
      );

      if (!provider) {
        setError("providerId", {
          type: "manual",
          message: t("validation:required-field"),
        });
        return;
      }

      const numericValue = Number(normalizeNumericInput(formData.value));

      const basePayload = {
        providerId: formData.providerId,
        providerName: provider.providerName,
        name: "ConvenienceFee",
        type: formData.type,
        percentage: formData.type === "Percentage" ? numericValue : null,
        amount: formData.type === "Fixed" ? numericValue : null,
        isActive: true,
      };

      let response;

      if (isEdit) {
        response = await putConvienceFee({
          id: selectedFee?.id,
          ...basePayload,
        });
      } else {
        response = await postConvienceFee(basePayload);
      }

      if (
        response?.message === "convenience-fee-already-configured" ||
        response?.errorMessage === "convenience-fee-already-configured"
      ) {
        setError("providerId", {
          type: "manual",
          message: t("validation:convenience-fee-already-configured"),
        });
        return;
      }

      if (response?.message === "convenience-fee-invalid-type") {
        setError("type", {
          type: "manual",
          message: t("validation:convenience-fee-invalid-type"),
        });
        return;
      }

      if (response?.code === 400 || response?.type === 1) {
        setError("providerId", {
          type: "manual",
          message: t("validation:convenience-fee-already-configured"),
        });
        return;
      }

      if (
        response?.code === 200 ||
        response?.status === "Success" ||
        response?.type === 0
      ) {
        await onSuccess();
        return;
      }

      setError("providerId", {
        type: "manual",
        message: t("validation:something-went-wrong"),
      });
    } catch (error) {
      setError("providerId", {
        type: "manual",
        message: t("validation:something-went-wrong"),
      });
    } finally {
      stopLoading({ btnName: "saveConvenienceFee" });
    }
  };

  const providerOptions = providers.map((provider) => ({
    key: provider.id,
    label: getProviderDisplayName(provider.providerName),
  }));

  const typeOptions = [
    {
      key: "Percentage",
      label: t("glossary:convinience-fee.percentage"),
    },
    {
      key: "Fixed",
      label: t("glossary:convinience-fee.fixed"),
    },
  ];

  return (
    <form onSubmit={handleSubmit(submitHandler)}>
      <Stack spacing={3}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 450,
              color: "#2F2F2F",
            }}
          >
            {isEdit
              ? `Edit Convenience Fee — ${getProviderDisplayName(
                  selectedFee?.providerName || "",
                )}`
              : "Add Convenience Fee"}
          </Typography>
        </Stack>

        <SelectInput
          control={control}
          label={t("glossary:convinience-fee.provider")}
          name="providerId"
          options={providerOptions}
          fullWidth
          required={true}
          placeholder={t("common:placeholder.select")}
          disabled={isEdit}
          showSelectLabel
          showAllLabel={false}
        />

        <SelectInput
          control={control}
          label={t("glossary:convinience-fee.type")}
          name="type"
          options={typeOptions}
          fullWidth
          required={true}
          placeholder={t("common:placeholder.select")}
          showSelectLabel
          disabled={isFirstDoctorProvider}
          showAllLabel={false}
        />

        {selectedType === "Fixed" ? (
          <NumberInputType4
            control={control}
            label={t("glossary:convinience-fee.amount")}
            name="value"
            fullWidth
            required={true}
            placeholder={t("glossary:convinience-fee.enter-amount")}
          />
        ) : (
          <NumberInputType3
            control={control}
            label={t("glossary:convinience-fee.percentage")}
            name="value"
            fullWidth
            required={true}
            placeholder={t("glossary:convinience-fee.enter-percentage")}
          />
        )}

        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button variant="LightBlueFontOnly" onClick={onCancel}>
            {t("common:button.cancel")}
          </Button>

          <LoadingButton
            loading={LoadingState.visible}
            type="submit"
            variant="primaryBlue"
            disabled={isEdit && !hasChanges}
          >
            {isEdit ? t("common:action.update") : t("common:action.add")}
          </LoadingButton>
        </Stack>
      </Stack>
    </form>
  );
};